import { useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { surfacesApi } from '@/services/api'
import { FormSection, StatePanel, LoadingSpinner } from '../common'
import SurfacePicker from './SurfacePicker'
import styles from './PublishToSection.module.css'


// Command Center renders the same "Publish to" surface picker per book but
// owns its own surface data (orgSurfaces), so it cannot mount this component.
// Sharing the copy keeps the two editors from drifting apart -- the whole
// point of presenting one layout "across the board".
export const PUBLISH_TO_TITLE = 'Publish to'
export const PUBLISH_TO_DESCRIPTION = 'Which feed surfaces should receive this post. Defaults are pre-selected from your feed settings.'


/**
 * PublishToSection renders a chip-picker for feed surfaces. The chips for
 * surfaces with `include_by_default = true` are pre-selected on a fresh post;
 * the caller owns the `surfaceIds` array via `onChange`.
 *
 * Props:
 * - orgSlug: current org slug (used for the surfaces query)
 * - surfaceIds: array of selected surface UUIDs, or null to auto-select defaults
 * - onChange: (nextIds: string[] | null) => void  — nextIds === null means reset to defaults
 * - onAvailabilityChange?: (hasEnabledSurfaces: boolean) => void
 * - onInitialSurfaceApplied?: (surfaceIds: string[]) => void
 * - canConfigureSurface: whether Configuration is available to this operator
 */
export default function PublishToSection({
  orgSlug,
  surfaceIds,
  onChange,
  onAvailabilityChange,
  onInitialSurfaceApplied,
  initialSurfaceSlug,
  canConfigureSurface = false,
}) {
  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['surfaces', orgSlug],
    queryFn: () => surfacesApi.list(orgSlug),
    enabled: !!orgSlug,
    staleTime: 30_000,
    refetchOnMount: 'always',
  })

  const allSurfaces = useMemo(() => data?.items || [], [data])
  const surfaces = useMemo(() => allSurfaces.filter((s) => s.is_enabled), [allSurfaces])
  const defaultIds = useMemo(
    () => surfaces.filter((surface) => surface.include_by_default).map((surface) => surface.id),
    [surfaces],
  )
  const initialSurface = useMemo(
    () => surfaces.find((surface) => surface.slug === initialSurfaceSlug),
    [initialSurfaceSlug, surfaces],
  )
  const requestKey = `${orgSlug || ''}:${initialSurfaceSlug || ''}`
  const activeRequestKey = useRef(requestKey)
  activeRequestKey.current = requestKey
  const isSurfaceDataPending = isLoading || isFetching
  const initialSurfaceUnavailable = Boolean(
    surfaceIds === null
    && initialSurfaceSlug
    && !isSurfaceDataPending
    && !isError
    && !initialSurface,
  )
  const selectedIds = surfaceIds === null
    ? initialSurfaceSlug
      ? initialSurface
        ? [initialSurface.id]
        : []
      : defaultIds
    : surfaceIds
  const latestSurfaceIdsRef = useRef(surfaceIds)
  latestSurfaceIdsRef.current = surfaceIds
  const initialSurfaceApplied = useRef('')
  if (surfaceIds !== null) initialSurfaceApplied.current = ''


  useEffect(() => {
    if (
      activeRequestKey.current !== requestKey
      || isSurfaceDataPending
      || isError
      || latestSurfaceIdsRef.current !== null
      || initialSurfaceApplied.current === requestKey
      || !initialSurfaceSlug
      || !initialSurface
    ) return
    initialSurfaceApplied.current = requestKey
    onInitialSurfaceApplied?.([initialSurface.id])
    onChange([initialSurface.id])
  }, [
    initialSurface,
    initialSurfaceSlug,
    isError,
    isSurfaceDataPending,
    onChange,
    requestKey,
    onInitialSurfaceApplied,
    surfaceIds,
  ])


  // Notify the parent immediately while fresh data is pending so terminal
  // actions cannot publish against a cached surface list.
  useEffect(() => {
    if (
      !onAvailabilityChange
      || activeRequestKey.current !== requestKey
      || latestSurfaceIdsRef.current !== surfaceIds
    ) return
    const selectedSurfacesExist = surfaceIds === null
      || surfaceIds.every((id) => surfaces.some((surface) => surface.id === id))
    onAvailabilityChange(
      !isSurfaceDataPending
      && !isError
      && !initialSurfaceUnavailable
      && selectedSurfacesExist
      && surfaces.length > 0,
    )
  }, [
    initialSurfaceUnavailable,
    isError,
    isSurfaceDataPending,
    onAvailabilityChange,
    requestKey,
    surfaceIds,
    surfaces,
  ])

  const toggleSurface = (id) => {
    const current = selectedIds || []
    const next = current.includes(id)
      ? current.filter((existing) => existing !== id)
      : [...current, id]
    onChange(next)
  }

  const configurationAction = canConfigureSurface
    ? <Link to="/dashboard/feed/configuration" className={styles.configureLink}>Open Configuration</Link>
    : null

  return (
    <FormSection
      title={PUBLISH_TO_TITLE}
      headingSize="subsection"
      description={PUBLISH_TO_DESCRIPTION}
    >
      {isSurfaceDataPending ? (
        <LoadingSpinner message="Loading surfaces..." />
      ) : isError ? (
        <StatePanel
          state="error"
          title="Surfaces could not be loaded"
          description="Retry before publishing this post."
          retryLabel="Retry surface load"
          onRetry={() => refetch()}
        />
      ) : initialSurfaceUnavailable ? (
        <StatePanel
          state="error"
          title="Target surface unavailable"
          description={canConfigureSurface
            ? `The ${initialSurfaceSlug} surface does not exist or is disabled. Retry, or choose an enabled surface in Configuration.`
            : `The ${initialSurfaceSlug} surface does not exist or is disabled. Contact an internal organization administrator to choose an enabled surface.`}
          action={configurationAction}
          retryLabel="Check again"
          onRetry={() => refetch()}
        />
      ) : surfaces.length === 0 ? (
        <StatePanel
          title="No feeds configured"
          description={canConfigureSurface
            ? 'Create a feed surface in Configuration before publishing.'
            : 'Contact an internal organization administrator to create an enabled feed surface.'}
          action={configurationAction}
        />
      ) : (
        <SurfacePicker
          surfaces={surfaces}
          selectedIds={selectedIds}
          onToggle={toggleSurface}
        />
      )}
    </FormSection>
  )
}
